import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import ScrollIndicator from "../components/ScrollIndicator";

const HomePage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const problems = [
    { icon: "💼", title: "Career & Direction", text: "Stagnation despite genuine effort and capability" },
    { icon: "💑", title: "Relationship & Marriage", text: "Obstacles in union, repeated mismatches" },
    { icon: "💰", title: "Financial Flow", text: "Money that arrives and disappears, cycles unresolved" },
    { icon: "🌿", title: "Health & Vitality", text: "Recurring ailments without conventional explanation" },
    { icon: "🏠", title: "Family Harmony", text: "Conflict and distance within the home" },
    { icon: "🎓", title: "Growth & Purpose", text: "Confusion about direction, missed opportunities" }
  ];

  const processSteps = [
    { num: "I", title: "Share Your Birth Details", desc: "Your name, date, time, place — and the one question on your heart. Nothing more is needed to begin." },
    { num: "II", title: "Root Cause Analysis", desc: "Deep chart study — not the surface challenge but the planetary configuration beneath it." },
    { num: "III", title: "We Explain First", desc: "Clear, jargon-free explanation of what your chart reveals. No fear. Understanding precedes recommendation." },
    { num: "IV", title: "You Choose Consciously", desc: "Only the remedy you accept will be performed. Never imposed. The choice is always yours." },
    { num: "V", title: "Performed & Documented", desc: "Your ritual conducted with care. Video and photo documentation delivered within 5–7 days." }
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="home-hero" data-testid="hero-section">
        <div className="home-hero-bg"></div>
        <div className="home-hero-mandala">
          <svg viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg" style={{width:'100%',height:'100%'}}>
            <circle cx="300" cy="300" r="290" stroke="#D4A843" strokeWidth=".6"/>
            <circle cx="300" cy="300" r="240" stroke="#D4A843" strokeWidth=".5"/>
            <circle cx="300" cy="300" r="190" stroke="#D4A843" strokeWidth=".5"/>
            <circle cx="300" cy="300" r="140" stroke="#D4A843" strokeWidth=".5"/>
            <circle cx="300" cy="300" r="90" stroke="#D4A843" strokeWidth=".5"/>
            <circle cx="300" cy="300" r="40" stroke="#D4A843" strokeWidth=".8"/>
            <line x1="300" y1="10" x2="300" y2="590" stroke="#D4A843" strokeWidth=".4" opacity=".5"/>
            <line x1="10" y1="300" x2="590" y2="300" stroke="#D4A843" strokeWidth=".4" opacity=".5"/>
            <line x1="88" y1="88" x2="512" y2="512" stroke="#D4A843" strokeWidth=".4" opacity=".4"/>
            <line x1="512" y1="88" x2="88" y2="512" stroke="#D4A843" strokeWidth=".4" opacity=".4"/>
            <circle cx="300" cy="300" r="10" fill="#D4A843" opacity=".4"/>
          </svg>
        </div>
        <div className="home-hero-content">
          <p className="home-hero-eyebrow" style={{animation:'fadeUp 1s ease .3s both'}}>Vedic Counsel &ensp;·&ensp; Sacred Remedies &ensp;·&ensp; Documented Truth</p>
          <h1 className="home-hero-title" style={{animation:'fadeUp 1s ease .5s both'}}>
            When the path<br/>feels <em>unclear —</em><br/>we illuminate it.
          </h1>
          <div className="home-hero-line"></div>
          <p className="home-hero-desc" style={{animation:'fadeUp 1s ease 1.1s both'}}>
            We listen to your birth chart as an ancient counselor would. Identify the planetary root beneath life's challenges. And offer remedies only when honestly justified — documented, delivered, trusted.
          </p>
          <div className="home-hero-btns" style={{animation:'fadeUp 1s ease 1.3s both'}}>
            <a
              href="https://wa.me/917633997555?text=Hi%20Amatya%2C%20I%20would%20like%20to%20book%20my%20Astro%20Analysis%20reading.%20Please%20share%20the%20next%20steps."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-book-btn"
              className="btn"
              style={{background:'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color:'#fff', border:'none', display:'inline-flex', alignItems:'center', gap:'8px', boxShadow:'0 6px 20px rgba(18, 140, 126, 0.28)'}}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg>
              Book Your Reading Now!!
            </a>
            <Link to="/begin-journey" className="btn btn-ember" data-testid="hero-begin-btn">
              What's Included in Your Consultation <span className="btn-arr">→</span>
            </Link>
            <Link to="/about" className="btn btn-ghost-lt" data-testid="hero-about-btn">Who We Are</Link>
          </div>
        </div>
        <div className="home-hero-stats" data-testid="hero-stats" style={{animation:'fadeUp 1s ease 1.5s both'}}>
          <div className="hero-stat" data-testid="hero-stat-lives">
            <span className="hero-stat-num">2,400+</span>
            <span className="hero-stat-label">Lives Guided</span>
          </div>
          <div className="hero-stat" data-testid="hero-stat-documented">
            <span className="hero-stat-num">100%</span>
            <span className="hero-stat-label">Rituals Documented</span>
          </div>
          <div className="hero-stat" data-testid="hero-stat-fear">
            <span className="hero-stat-num">0</span>
            <span className="hero-stat-label">Fear-Based Predictions</span>
          </div>
        </div>
        <div 
          className="scroll-indicator" 
          data-testid="scroll-indicator"
          onClick={() => document.getElementById('problems-section')?.scrollIntoView({ behavior: 'smooth' })}
          style={{animation:'fadeUp 1s ease 1.8s both'}}
        >
          <span className="scroll-indicator-text">Scroll</span>
          <div className="scroll-indicator-arrow"></div>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="problems-section" id="problems-section" data-testid="problems-section">
        <div className="wrap">
          <div className="problems-grid">
            <div className="reveal">
              <span className="section-label">We Understand</span>
              <h2 className="section-title">When life refuses<br/>to move forward</h2>
              <p className="section-body" style={{marginTop:'22px'}}>
                You have been sincere. You have worked. And yet certain areas remain inexplicably stuck — as if an invisible hand keeps returning you to the same place.
              </p>
              <div className="problems-quote">
                <p className="problems-quote-text">"Most problems visible to us are symptoms of a root unseen."</p>
                <cite className="problems-quote-cite">— Amatya Counsel</cite>
              </div>
            </div>
            <div className="problem-cards reveal d2">
              {problems.map((p, i) => (
                <div key={i} className="problem-card" data-testid={`problem-card-${i}`}>
                  <span className="problem-card-icon">{p.icon}</span>
                  <div className="problem-card-title">{p.title}</div>
                  <div className="problem-card-text">{p.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to Process */}
      <div style={{background:'var(--white)'}}>
        <ScrollIndicator targetId="process-section" />
      </div>

      {/* PROCESS */}
      <section className="process-section" id="process-section" data-testid="process-section">
        <div className="wrap">
          <div className="process-grid">
            <div>
              <span className="section-label reveal" style={{color:'rgba(212,168,67,.75)'}}>How We Work</span>
              <h2 className="process-title reveal d1">A process built<br/>on clarity &<br/>your consent</h2>
              <p className="process-intro reveal d2">
                We never impose. We never obscure. Every step is explained before it is taken. You are a participant, not a patient.
              </p>
              <Link to="/begin-journey" className="btn btn-ember reveal" style={{marginTop:'40px'}} data-testid="process-begin-btn">
                Begin Consultation <span className="btn-arr">→</span>
              </Link>
            </div>
            <div className="process-steps reveal d2">
              {processSteps.map((step, i) => (
                <div key={i} className="process-step" data-testid={`process-step-${i}`}>
                  <div className="process-step-num">{step.num}</div>
                  <div>
                    <div className="process-step-title">{step.title}</div>
                    <div className="process-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Scroll to CTA */}
      <ScrollIndicator targetId="bottom-cta" />

      {/* BOTTOM CTA */}
      <div className="bottom-cta" id="bottom-cta" data-testid="bottom-cta">
        <div style={{position:'relative',zIndex:2}} className="reveal">
          <span className="bc-dev">॥ ॐ गं गणपतये नमः ॥</span>
          <h2 className="bc-title">Begin your path to <em>clarity & peace</em></h2>
          <p className="bc-body">One honest conversation with your birth chart. Explained clearly. Remedies offered only when warranted. Documented when performed.</p>
          <div className="bc-btns">
            <Link to="/begin-journey" className="btn btn-ember" data-testid="cta-begin-btn">
              Request Root Cause Analysis — ₹2,101 <span className="btn-arr">→</span>
            </Link>
            <Link to="/services" className="btn btn-ghost-lt" data-testid="cta-services-btn">Explore Services</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
